import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useUpdateAppointmentStatus } from "../../queries/use-change-appointment-status";
import {
  getErrorSnackbar,
  getPatientVisitNoteUrl,
  getXtenantId,
} from "../../utils/utils";
import { RoomParams } from "../types";
import TelehealthOverlay from "./TelehealthOverlay";

/**
 * In-app meeting launcher. Owns a single `<TelehealthOverlay>` that lives
 * inside whichever shell mounts the provider — typically PatientPortalLayout
 * or ProviderPortalLayout. Because the overlay is rendered at the shell
 * level (and portals to body), it survives nested-route navigation, so the
 * user can shrink the call to PiP and keep clicking around their portal
 * without losing the meeting.
 *
 * When `storageKey` is set, the launcher mirrors its current launch options
 * to localStorage — so a full page refresh re-opens the meeting silently
 * (skipPreflight=true is forced on restore so the host auto-joins straight
 * back, and the patient auto-rejoins the lobby and resumes knocking via
 * useLobbyChannel's normal phase=lobby loop).
 *
 * When `hostActions` is set, the launcher wires the host action bar's
 * Cancel/NoShow/StartEncounter/Minimize handlers internally so the host's
 * recovered overlay still has the full set of in-call controls. Patient
 * shells leave it false.
 *
 * Exposed via `useTelehealthLauncher()`:
 *   - start({ params, ... }) — open the overlay with the given session
 *   - stop()                 — close the overlay and clear persistence
 */
interface LaunchOptions {
  params: RoomParams;
  patientName?: string;
  providerName?: string;
  /** True when the session signature must be fetched without auth headers
   *  (open guest URLs). Logged-in patient/provider launches leave this
   *  false so the signature endpoint sees the session token. */
  isPublicView?: boolean;
}

interface LauncherApi {
  start: (opts: LaunchOptions) => void;
  stop: () => void;
  isOpen: boolean;
}

interface ProviderProps {
  children: ReactNode;
  /** localStorage key for auto-recovery on refresh. Patient and provider
   *  portals use distinct keys so a user switching viewAs doesn't pick up
   *  the wrong shell's stashed meeting. */
  storageKey?: string;
  /** Wires the host-only Cancel/NoShow/StartEncounter/Minimize callbacks
   *  on the rendered overlay. Only meaningful when launches in this shell
   *  carry role=host plus appointmentId+patientId. */
  hostActions?: boolean;
}

const TelehealthLauncherContext = createContext<LauncherApi | undefined>(
  undefined
);

function readStored(storageKey?: string): LaunchOptions | undefined {
  if (!storageKey) return undefined;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed?.params?.room) return undefined;
    return parsed as LaunchOptions;
  } catch {
    return undefined;
  }
}

/** True only when this page was loaded via a page refresh — i.e. the
 *  user pressed reload on a tab that already had a meeting active.
 *  Internal tab navigations (clicking around the app, opening a fresh
 *  tab and logging in, back/forward) all return false, so we don't
 *  pop a ghost meeting on top of a normal portal landing from a stale
 *  localStorage entry left by a tab the user closed without leaving. */
function isPageReload(): boolean {
  try {
    const entries = performance.getEntriesByType("navigation") as Array<{
      type?: string;
    }>;
    return entries[0]?.type === "reload";
  } catch {
    return false;
  }
}

function writeStored(storageKey: string | undefined, opts: LaunchOptions | undefined) {
  if (!storageKey) return;
  try {
    if (opts) localStorage.setItem(storageKey, JSON.stringify(opts));
    else localStorage.removeItem(storageKey);
  } catch {
    /* localStorage may be disabled — feature degrades to in-memory only */
  }
}

export function TelehealthLauncherProvider({
  children,
  storageKey,
  hostActions = false,
}: ProviderProps) {
  // Initialize from storage so a hard refresh skips the empty-state flash
  // and goes straight to the recovered overlay. skipPreflight is forced
  // on so the user re-enters the call without seeing the "Ready to join?"
  // screen — they've already chosen to be in the meeting.
  //
  // Crucially, we ONLY auto-restore on an actual page reload. A normal
  // portal landing (login, tab switch, navigate-back) shouldn't pop a
  // ghost meeting from a stale localStorage entry — the user might have
  // closed a previous tab without explicitly leaving, and that stale
  // entry would otherwise auto-launch on top of the next "Start Visit"
  // click and confuse the freshly-launched session.
  const [opts, setOpts] = useState<LaunchOptions | undefined>(() => {
    if (!isPageReload()) {
      // Drop any stale entry so it doesn't keep tripping us on later
      // mounts; the user can re-join through the normal entry points.
      writeStored(storageKey, undefined);
      return undefined;
    }
    const stored = readStored(storageKey);
    if (!stored) return undefined;
    return {
      ...stored,
      params: { ...stored.params, skipPreflight: true },
    };
  });

  const start = useCallback(
    (next: LaunchOptions) => {
      setOpts(next);
      writeStored(storageKey, next);
    },
    [storageKey]
  );
  const stop = useCallback(() => {
    setOpts(undefined);
    writeStored(storageKey, undefined);
  }, [storageKey]);

  // Cross-tab sync: if the user closes the meeting in another tab, this
  // tab should drop its overlay too instead of holding a now-defunct call.
  useEffect(() => {
    if (!storageKey) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      if (!e.newValue) setOpts(undefined);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  // ----- host action bar (no-ops when hostActions=false) -----------------
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();

  const wireActions =
    hostActions &&
    opts?.params.role === "host" &&
    !!opts.params.appointmentId &&
    !!opts.params.patientId;

  const invalidateAppointments = () => {
    queryClient.invalidateQueries({ queryKey: ["appointment-list"] });
    queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
  };

  const handleCancelAppointment = wireActions
    ? async (reason: string) => {
        try {
          await updateStatus({
            appointmentId: opts!.params.appointmentId!,
            status: "CANCELLED",
            reason,
            xTENANTID: getXtenantId(),
          });
          invalidateAppointments();
          stop();
        } catch (err) {
          showSnackbar(getErrorSnackbar(err));
          throw err;
        }
      }
    : undefined;

  const handleNoShow = wireActions
    ? async () => {
        try {
          await updateStatus({
            appointmentId: opts!.params.appointmentId!,
            status: "NO_SHOW",
            xTENANTID: getXtenantId(),
          });
          invalidateAppointments();
          stop();
        } catch (err) {
          showSnackbar(getErrorSnackbar(err));
          throw err;
        }
      }
    : undefined;

  const handleStartEncounter = wireActions
    ? async () => {
        try {
          await updateStatus({
            appointmentId: opts!.params.appointmentId!,
            status: "IN_EXAM",
            xTENANTID: getXtenantId(),
          });
          invalidateAppointments();
        } catch (err) {
          showSnackbar(getErrorSnackbar(err));
          // Fall through: still navigate so the host isn't stuck if the
          // status update happens to fail — matches the existing flow.
        }
        navigate(
          getPatientVisitNoteUrl(opts!.params.patientId!, opts!.params.appointmentId!)
        );
      }
    : undefined;

  const handleMinimize = wireActions
    ? () => {
        navigate(
          getPatientVisitNoteUrl(opts!.params.patientId!, opts!.params.appointmentId!)
        );
      }
    : undefined;

  return (
    <TelehealthLauncherContext.Provider
      value={{ start, stop, isOpen: !!opts }}
    >
      {children}
      {opts && (
        <TelehealthOverlay
          open
          params={opts.params}
          isPublicView={opts.isPublicView}
          patientName={opts.patientName}
          providerName={opts.providerName}
          onClose={stop}
          // Mini-mode is the whole point of launching from the shell:
          // shrink the call and let the user keep using the portal.
          allowMiniMode
          onCancelAppointment={handleCancelAppointment}
          onNoShow={handleNoShow}
          onStartEncounter={handleStartEncounter}
          onMinimize={handleMinimize}
        />
      )}
    </TelehealthLauncherContext.Provider>
  );
}

export function useTelehealthLauncher(): LauncherApi {
  const ctx = useContext(TelehealthLauncherContext);
  if (ctx) return ctx;
  // Fallback so consumers (e.g. modals mounted near the shell boundary)
  // don't crash on render when the provider isn't in the tree. start()
  // becomes a no-op that surfaces the misconfiguration in the console
  // instead of throwing during render.
  return {
    start: () => {
      console.error(
        "useTelehealthLauncher.start called outside <TelehealthLauncherProvider>"
      );
    },
    stop: () => {},
    isOpen: false,
  };
}
